/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { MutableRef } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type { SelectionDetail } from '#UNSAFE_Collection';
import type { ListSelectionMode } from '../List.types';
import type { ActiveCell, ActiveCellDetail, ScrollDetail } from '../List';

import { keyExtractor } from '#utils/PRIVATE_collectionUtils';
import { addKey, containsKey, isSameKey, removeKey } from '#utils/UNSAFE_keys';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getElementFromCell,
  getFirstFocusableCell,
  getRowKeyForRowIndex,
  getRowIndexForRowElement,
  LIST_ROW_SELECTOR,
  logicalCellExtractor,
  rowExtractor
} from './ListDomUtils';

/**
 * Helper function to handle a selection gesture (click, spacebar, etc...)
 */
export function handleSelectionGesture<K extends string | number>(
  listId: string,
  targetElement: HTMLElement,
  isModifierKey: boolean,
  onSelectionChange: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: ListSelectionMode,
  selectedKeys?: Keys<K>
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selectedKeys != null ? selectedKeys : (emptyKeys as Keys<K>);

  const newRowKey = keyExtractor<K>(targetElement, `[${LIST_ROW_SELECTOR}='${listId}']`);
  if (newRowKey != null) {
    if (
      selectionMode === 'single' ||
      selectionMode === 'multiple' ||
      selectionMode === 'multipleToggle'
    ) {
      const isToggleSelectionMode = isDropDownSelection || selectionMode === 'multipleToggle';
      const isMultiSelectRowGesture = isModifierKey || isToggleSelectionMode;
      if (selectedRows == null) {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      } else if (!containsKey(selectedRows, newRowKey)) {
        if (isMultiSelectRowGesture && selectionMode !== 'single') {
          selectedRows = addKey(selectedRows, newRowKey);
        } else {
          selectedRows = { all: false, keys: new Set([newRowKey]) };
        }
      } else if (isMultiSelectRowGesture) {
        selectedRows = removeKey(selectedRows, newRowKey, false);
      } else {
        selectedRows = { all: false, keys: new Set([newRowKey]) };
      }
    }
  }
  if (!isSameKey(selectedRows, selectedKeys)) {
    onSelectionChange({ value: selectedRows, target: targetElement }, false);
  }
}

/**
 * Helper function to calculate and apply a new selection state corresponding to the range provided
 */
function _applyRangeSelection<K extends string | number>(
  rootElement: HTMLDivElement,
  listId: string,
  targetElement: HTMLElement,
  startIndex: number,
  endIndex: number,
  isRowGesture: boolean,
  onSelectionChange: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: ListSelectionMode,
  selectedKeys?: Keys<K>,
  previousEndIndex?: number
) {
  const emptyKeys = { all: false, keys: new Set() };
  let selectedRows = selectedKeys != null ? selectedKeys : (emptyKeys as Keys<K>);

  if (isRowGesture) {
    if (selectionMode === 'multiple' || selectionMode === 'multipleToggle') {
      const isToggleSelectionMode = isDropDownSelection || selectionMode === 'multipleToggle';
      selectedRows = isToggleSelectionMode ? selectedRows : (emptyKeys as Keys<K>);
      // selected rows should be added in the order that they are selected
      if (startIndex <= endIndex) {
        for (let i = startIndex; i <= endIndex; i++) {
          const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
        // toggle selection gesture is additive except for rows added from previous gesture
        if (isToggleSelectionMode && previousEndIndex != null) {
          if (previousEndIndex > endIndex) {
            for (let i = previousEndIndex; i > endIndex; i--) {
              const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          } else if (previousEndIndex < startIndex) {
            for (let i = previousEndIndex; i < startIndex; i++) {
              const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          }
        }
      } else {
        for (let i = startIndex; i >= endIndex; i--) {
          const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
          selectedRows = addKey(selectedRows, key) as Keys<K>;
        }
        // toggle selection gesture is additive except for rows added from previous gesture
        if (isToggleSelectionMode && previousEndIndex != null) {
          if (previousEndIndex < endIndex) {
            for (let i = previousEndIndex; i < endIndex; i++) {
              const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          } else if (previousEndIndex > startIndex) {
            for (let i = previousEndIndex; i > startIndex; i--) {
              const key = getRowKeyForRowIndex<K>(rootElement, listId, i);
              selectedRows = removeKey(selectedRows, key, false) as Keys<K>;
            }
          }
        }
      }
    }
  }

  if (!isSameKey(selectedRows, selectedKeys)) {
    onSelectionChange({ value: selectedRows, target: targetElement }, true);
  }
}

/**
 * Determines whether a potential range selection gesture is valid based on the given target
 */
export function isRangeSelectionGesture<K extends string | number>(
  rootElement: HTMLDivElement,
  listId: string,
  targetElement: HTMLElement,
  activeCell: ActiveCell<K>,
  selectionMode?: ListSelectionMode
) {
  const targetRowElement = rowExtractor(targetElement, listId);
  if (
    (selectionMode === 'multiple' || selectionMode === 'multipleToggle') &&
    activeCell.type === 'data' &&
    targetRowElement != null
  ) {
    const activeCellElement = getElementFromCell(rootElement, listId, activeCell);
    if (activeCellElement != null && targetRowElement != null) {
      return (
        getRowIndexForRowElement(
          rootElement,
          listId,
          activeCellElement.parentElement as HTMLElement
        ) != null && getRowIndexForRowElement(rootElement, listId, targetRowElement) != null
      );
    }
  }
  return false;
}

/**
 * Helper function to handle a range selection gesture (shift+click)
 */
export function handleRangeSelectionGesture<K extends string | number>(
  rootElement: HTMLDivElement,
  listId: string,
  targetElement: HTMLElement,
  activeCell: ActiveCell<K>,
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  selectionMode?: ListSelectionMode,
  selectedKeys?: Keys<K>
) {
  const targetRowElement = rowExtractor(targetElement, listId);
  if (targetRowElement != null) {
    const activeCellElement = getElementFromCell(rootElement, listId, activeCell);
    if (activeCellElement != null) {
      if (
        (selectionMode === 'multiple' || selectionMode === 'multipleToggle') &&
        activeCell.type === 'data'
      ) {
        let previousEndIndex;
        const isToggleSelectionMode = isDropDownSelection || selectionMode === 'multipleToggle';
        const endElement = targetRowElement;
        const startElement =
          isToggleSelectionMode && selectionExtensionElementRef.current != null
            ? selectionExtensionElementRef.current
            : (activeCellElement.parentElement as HTMLElement);
        const startIndex = getRowIndexForRowElement(rootElement, listId, startElement);
        const endIndex = getRowIndexForRowElement(rootElement, listId, endElement);
        const previousElement = isToggleSelectionMode
          ? (activeCellElement.parentElement as HTMLElement)
          : selectionExtensionElementRef.current;
        if (previousElement != null) {
          previousEndIndex = getRowIndexForRowElement(rootElement, listId, previousElement);
        }
        if (startIndex != null && endIndex != null) {
          if (!isToggleSelectionMode) {
            selectionExtensionElementRef.current = endElement;
          } else {
            if (selectionExtensionElementRef.current == null) {
              selectionExtensionElementRef.current = activeCellElement.parentElement as HTMLElement;
            }
            let targetCell = logicalCellExtractor<K>(targetElement, listId);
            if (targetCell == null) {
              targetCell = getFirstFocusableCell<K>(targetRowElement, listId) as ActiveCell<K>;
            }
            if (targetCell != null) {
              onActiveCellChanged({ value: targetCell }, false, true);
              const targetCellElement = getElementFromCell(rootElement, listId, targetCell);
              targetCellElement?.focus({ preventScroll: true });
            }
          }
          _applyRangeSelection(
            rootElement,
            listId,
            targetElement,
            startIndex,
            endIndex,
            true,
            onSelectionChange,
            isDropDownSelection,
            selectionMode,
            selectedKeys,
            previousEndIndex
          );
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Helper function to handle a selection extension gesture (shift+arrow key)
 */
export function handleExtendSelectionGesture<K extends string | number>(
  rootElement: HTMLDivElement,
  listId: string,
  targetElement: HTMLElement,
  isPrevious: boolean,
  activeCell: ActiveCell<K>,
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>,
  onSelectionChange: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void,
  isDropDownSelection: boolean,
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K>) => void,
  selectionMode?: ListSelectionMode,
  selectedKeys?: Keys<K>
) {
  const activeCellElement = getElementFromCell(rootElement, listId, activeCell);
  if (activeCellElement != null) {
    let startIndex;
    let startElement;
    const isToggleSelectionMode = isDropDownSelection || selectionMode === 'multipleToggle';
    const isRow = activeCell.type === 'data';
    if (isRow) {
      startElement =
        isToggleSelectionMode && selectionExtensionElementRef.current != null
          ? selectionExtensionElementRef.current
          : (activeCellElement.parentElement as HTMLElement);
      startIndex = getRowIndexForRowElement(rootElement, listId, startElement);
    }
    if (startIndex != null) {
      let endIndex;
      let previousEndIndex;
      let extendedElement;
      const cursorElement =
        isToggleSelectionMode && isRow
          ? (activeCellElement.parentElement as HTMLElement)
          : selectionExtensionElementRef.current;
      if (cursorElement != null) {
        previousEndIndex = getRowIndexForRowElement(rootElement, listId, cursorElement);
        extendedElement = isPrevious
          ? (cursorElement.previousElementSibling as HTMLElement)
          : (cursorElement.nextElementSibling as HTMLElement);
        if (extendedElement != null && (!isRow || (isRow && getRowKey(extendedElement) != null))) {
          if (!isToggleSelectionMode || !isRow) {
            selectionExtensionElementRef.current = extendedElement;
          } else if (selectionExtensionElementRef.current == null) {
            selectionExtensionElementRef.current = activeCellElement.parentElement as HTMLElement;
          }
          endIndex = getRowIndexForRowElement(rootElement, listId, extendedElement);
        } else {
          endIndex = getRowIndexForRowElement(rootElement, listId, cursorElement);
        }
      } else {
        extendedElement = isPrevious
          ? (startElement?.previousElementSibling as HTMLElement)
          : (startElement?.nextElementSibling as HTMLElement);
        if (extendedElement != null && (!isRow || (isRow && getRowKey(extendedElement) != null))) {
          selectionExtensionElementRef.current = extendedElement;
          endIndex = getRowIndexForRowElement(rootElement, listId, extendedElement);
        } else {
          endIndex = startIndex;
        }
      }
      if (endIndex != null) {
        _applyRangeSelection(
          rootElement,
          listId,
          targetElement,
          startIndex,
          endIndex,
          isRow,
          onSelectionChange,
          isDropDownSelection,
          selectionMode,
          selectedKeys,
          previousEndIndex
        );
      }

      // scroll 'extensionElement' into view if focus is not shifted
      if (!isRow || !isToggleSelectionMode) {
        const extensionElement =
          selectionExtensionElementRef.current != null
            ? selectionExtensionElementRef.current
            : activeCellElement;
        let extensionCell;
        if (isRow) {
          const rowKey = keyExtractor<K>(extensionElement, `[${LIST_ROW_SELECTOR}='${listId}']`);
          if (rowKey != null) {
            extensionCell = {
              type: activeCell.type,
              rowKey: rowKey
            };
          }
        } else {
          extensionCell = logicalCellExtractor<K>(extensionElement, listId);
        }
        if (extensionCell != null) {
          updateDeferredScrollDetail({
            cell: extensionCell,
            locationX: 'inView',
            locationY: 'inView'
          });
        }
      }
    }
  }
}
